/**
 * LLM Configuration Service - v13 Section 6.10 (Sprint 11g)
 *
 * Dynamic configuration service for LLM models and pricing.
 * Implements the "Rosetta Stone" pattern:
 *
 * 1. Fetch model IDs from Provider APIs (source of truth)
 * 2. Fetch metadata from OpenRouter (pricing, context windows)
 * 3. Merge: Real IDs + enriched metadata
 *
 * @example
 * ```typescript
 * import { configService } from '@ownyou/llm-client';
 *
 * // Refresh models with provider API keys (Rosetta Stone pattern)
 * const models = await configService.refreshModels({
 *   anthropic: 'sk-ant-...',
 *   openai: 'sk-...',
 *   google: 'AIza...',
 * });
 *
 * // Get pricing for cost calculation
 * const pricing = await configService.getPricing('gpt-4o-mini');
 *
 * // Get cached models (sync)
 * const models = configService.getModels();
 * ```
 *
 * @see docs/architecture/extracts/llm-cost-6.10.md
 * @see docs/sprints/ownyou-sprint11g-spec.md
 */
import { BUNDLED_DEFAULTS, DEFAULT_CACHE_TTL_MS } from './defaults';
import { fetchFromLLMPrices } from './sources/llmPrices';
import { fetchFromOpenRouter, fetchMetadataMap, normalizeModelId } from './sources/openRouter';
import { fetchFromAnthropic } from './sources/anthropic';
import { fetchFromOpenAI } from './sources/openai';
import { fetchFromGoogle } from './sources/google';
import { fetchFromGroq } from './sources/groq';
import { fetchFromDeepinfra } from './sources/deepinfra';
import { fetchGenAIPricing, } from './sources/genaiPrices';
/**
 * Simple in-memory storage for environments without IndexedDB
 */
class InMemoryStorage {
    constructor() {
        this.store = new Map();
    }
    async get(key) {
        return this.store.get(key) ?? null;
    }
    async set(key, value) {
        this.store.set(key, value);
    }
    async remove(key) {
        this.store.delete(key);
    }
}
/**
 * IndexedDB storage for browser environments
 */
class IndexedDBStorage {
    constructor() {
        this.dbName = 'ownyou-config';
        this.storeName = 'llm-config';
        this.db = null;
    }
    async getDB() {
        if (this.db)
            return this.db;
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, 1);
            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };
            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName);
                }
            };
        });
    }
    async get(key) {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, 'readonly');
                const store = tx.objectStore(this.storeName);
                const request = store.get(key);
                request.onerror = () => reject(request.error);
                request.onsuccess = () => resolve(request.result ?? null);
            });
        }
        catch {
            return null;
        }
    }
    async set(key, value) {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, 'readwrite');
                const store = tx.objectStore(this.storeName);
                const request = store.put(value, key);
                request.onerror = () => reject(request.error);
                request.onsuccess = () => resolve();
            });
        }
        catch {
            // Silently fail storage - memory cache will still work
        }
    }
    async remove(key) {
        try {
            const db = await this.getDB();
            return new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, 'readwrite');
                const store = tx.objectStore(this.storeName);
                const request = store.delete(key);
                request.onerror = () => reject(request.error);
                request.onsuccess = () => resolve();
            });
        }
        catch {
            // Silently fail
        }
    }
}
/**
 * Create appropriate storage for the environment
 */
const createStorage = () => {
    if (typeof indexedDB !== 'undefined') {
        return new IndexedDBStorage();
    }
    return new InMemoryStorage();
};
const STORAGE_KEY = 'llm-provider-config';
/**
 * ConfigService - Singleton for LLM configuration management
 *
 * Sprint 11g: Implements Rosetta Stone pattern for model management:
 * - Provider APIs are source of truth for model IDs
 * - OpenRouter provides metadata enrichment (pricing, context windows)
 * - ConfigService merges these for the unified model list
 */
class ConfigServiceImpl {
    constructor() {
        this.memoryCache = null;
        this.memoryCacheExpiry = 0;
        this.fetchPromise = null;
        // Sprint 11g: Rosetta Stone model cache
        this.modelCache = [];
        this.llmPricesConfig = null;
        this.genaiPricing = null;
        this.storage = createStorage();
    }
    /**
     * Get the full configuration (cached)
     */
    async getConfig() {
        // 1. Check memory cache
        if (this.memoryCache && Date.now() < this.memoryCacheExpiry) {
            return this.memoryCache;
        }
        // 2. Check storage cache
        try {
            const stored = await this.storage.get(STORAGE_KEY);
            if (stored) {
                const { config, expiry } = JSON.parse(stored);
                if (Date.now() < expiry) {
                    this.memoryCache = config;
                    this.memoryCacheExpiry = expiry;
                    return config;
                }
            }
        }
        catch {
            // Storage error - continue to fetch
        }
        // 3. Fetch from remote (with deduplication)
        if (!this.fetchPromise) {
            this.fetchPromise = this.fetchAndCache();
        }
        try {
            return await this.fetchPromise;
        }
        finally {
            this.fetchPromise = null;
        }
    }
    /**
     * Fetch from remote sources and cache
     *
     * Strategy: Fetch from both sources in parallel and merge:
     * - OpenRouter provides rich metadata (contextWindow, maxCompletionTokens, isReasoningModel)
     * - llm-prices.com provides frequently updated pricing
     *
     * The merged result uses OpenRouter as the base (for metadata) and
     * overlays llm-prices.com pricing (which is more current).
     */
    async fetchAndCache() {
        // Fetch from both sources in parallel
        const [llmPricesResult, openRouterResult] = await Promise.allSettled([
            fetchFromLLMPrices(),
            fetchFromOpenRouter(),
        ]);
        let config;
        // Determine base config - prefer OpenRouter (has richer metadata)
        if (openRouterResult.status === 'fulfilled') {
            config = openRouterResult.value;
        }
        else if (llmPricesResult.status === 'fulfilled') {
            console.warn('OpenRouter failed, using llm-prices.com only:', openRouterResult.reason);
            config = llmPricesResult.value;
        }
        else {
            // Both failed - use bundled defaults
            console.warn('All sources failed, using bundled defaults');
            console.warn('llm-prices.com error:', llmPricesResult.reason);
            console.warn('OpenRouter error:', openRouterResult.reason);
            config = { ...BUNDLED_DEFAULTS, timestamp: Date.now() };
            await this.cacheConfig(config);
            return config;
        }
        // Merge llm-prices.com pricing into OpenRouter metadata
        // llm-prices.com pricing is more frequently updated
        if (llmPricesResult.status === 'fulfilled' && openRouterResult.status === 'fulfilled') {
            const llmPricesConfig = llmPricesResult.value;
            for (const [modelId, llmPricesMetadata] of Object.entries(llmPricesConfig.models)) {
                if (config.models[modelId]) {
                    // Model exists in both - prefer llm-prices.com pricing (more current)
                    if (llmPricesMetadata.pricing) {
                        config.models[modelId].pricing = llmPricesMetadata.pricing;
                    }
                }
                else {
                    // Model only in llm-prices.com - add it (pricing-only, no metadata)
                    config.models[modelId] = llmPricesMetadata;
                }
            }
            // Update source to indicate merged data
            config.source = 'openrouter'; // Primary source for metadata
        }
        // Cache the result
        await this.cacheConfig(config);
        return config;
    }
    /**
     * Cache configuration in memory and storage
     */
    async cacheConfig(config) {
        const expiry = Date.now() + DEFAULT_CACHE_TTL_MS;
        // Memory cache
        this.memoryCache = config;
        this.memoryCacheExpiry = expiry;
        // Storage cache
        try {
            await this.storage.set(STORAGE_KEY, JSON.stringify({ config, expiry }));
        }
        catch {
            // Storage error - memory cache still works
        }
    }
    /**
     * Force refresh from remote sources
     */
    async forceRefresh() {
        // Clear caches
        this.memoryCache = null;
        this.memoryCacheExpiry = 0;
        await this.storage.remove(STORAGE_KEY);
        // Fetch fresh
        return this.getConfig();
    }
    /**
     * Get cache status
     */
    getCacheStatus() {
        return {
            cached: this.memoryCache !== null && Date.now() < this.memoryCacheExpiry,
            expiry: this.memoryCacheExpiry > 0 ? new Date(this.memoryCacheExpiry) : null,
            source: this.memoryCache?.source ?? 'none',
        };
    }
    /**
     * Get pricing for a model
     * Returns zero pricing if not available from API
     */
    async getPricing(modelId) {
        const config = await this.getConfig();
        const model = config.models[modelId];
        if (model?.pricing) {
            return model.pricing;
        }
        // Try to find by partial match (e.g., "gpt-4o" matches "gpt-4o-mini")
        for (const [id, metadata] of Object.entries(config.models)) {
            if (id.startsWith(modelId) || modelId.startsWith(id)) {
                if (metadata.pricing) {
                    return metadata.pricing;
                }
            }
        }
        // Return zero pricing for unknown models or when pricing not available
        return { inputPer1M: 0, outputPer1M: 0 };
    }
    /**
     * Get metadata for a model
     * Supports partial matching for dated model versions
     * e.g., "gpt-5-nano-2025-08-07" matches "gpt-5-nano"
     */
    async getModelMetadata(modelId) {
        const config = await this.getConfig();
        // Exact match first
        if (config.models[modelId]) {
            return config.models[modelId];
        }
        // Partial match: find models where one is prefix of the other
        // This handles dated versions: "gpt-5-nano-2025-08-07" matches "gpt-5-nano"
        for (const [id, metadata] of Object.entries(config.models)) {
            if (id.startsWith(modelId) || modelId.startsWith(id)) {
                return metadata;
            }
        }
        return null;
    }
    /**
     * Get models for a provider
     */
    async getModelsByProvider(provider) {
        const config = await this.getConfig();
        return Object.entries(config.models)
            .filter(([_, metadata]) => metadata.provider === provider)
            .map(([id]) => id);
    }
    /**
     * Get models for a tier
     */
    async getModelsByTier(tier) {
        const config = await this.getConfig();
        const tierConfig = config.tiers[tier];
        if (!tierConfig)
            return [];
        return [tierConfig.primaryModel, ...tierConfig.fallbackModels];
    }
    /**
     * Get fallback models for UI
     */
    async getFallbackModels() {
        const config = await this.getConfig();
        return config.fallbackModels;
    }
    /**
     * Check if a model exists in config
     */
    async hasModel(modelId) {
        const config = await this.getConfig();
        return modelId in config.models;
    }
    /**
     * Get all model IDs
     */
    async getAllModelIds() {
        const config = await this.getConfig();
        return Object.keys(config.models);
    }
    /**
     * Get zero data retention models
     */
    async getZDRModels() {
        const config = await this.getConfig();
        return Object.entries(config.models)
            .filter(([_, metadata]) => metadata.zeroDataRetention)
            .map(([id]) => id);
    }
    /**
     * Get reasoning models
     */
    async getReasoningModels() {
        const config = await this.getConfig();
        return Object.entries(config.models)
            .filter(([_, metadata]) => metadata.isReasoningModel)
            .map(([id]) => id);
    }
    // =========================================================================
    // SPRINT 11g: ROSETTA STONE PATTERN
    // Provider APIs = source of truth for IDs
    // OpenRouter = source for metadata enrichment
    // =========================================================================
    /**
     * Refresh models using the Rosetta Stone pattern
     *
     * STEP 1: Fetch metadata map from OpenRouter (pricing, context windows)
     * STEP 2: Fetch model IDs from Provider APIs (the REAL IDs)
     * STEP 3: Merge: Real IDs enriched with OpenRouter metadata
     *
     * @param apiKeys - API keys for each provider to authenticate
     * @returns Array of EnrichedModel with real IDs and metadata
     */
    async refreshModels(apiKeys) {
        console.log('[ConfigService] Starting Rosetta Stone model refresh...');
        // STEP 1: Fetch metadata from OpenRouter AND llm-prices.com (parallel with providers)
        const metadataMapPromise = fetchMetadataMap().catch((err) => {
            console.warn('[ConfigService] OpenRouter metadata fetch failed:', err);
            return new Map();
        });
        // Also fetch from llm-prices.com as pricing fallback
        const llmPricesPromise = fetchFromLLMPrices().catch((err) => {
            console.warn('[ConfigService] llm-prices.com fetch failed:', err);
            return null;
        });
        // Fetch pricing from pydantic/genai-prices (CORS-friendly GitHub raw)
        const genaiPricingPromise = fetchGenAIPricing().catch((err) => {
            console.warn('[ConfigService] genai-prices fetch failed:', err);
            return null;
        });
        // STEP 2: Fetch models from Provider APIs in parallel
        const providerFetchers = [];
        if (apiKeys.anthropic) {
            providerFetchers.push({
                provider: 'anthropic',
                promise: fetchFromAnthropic(apiKeys.anthropic).catch((err) => {
                    console.warn('[ConfigService] Anthropic fetch failed:', err);
                    return [];
                }),
            });
        }
        if (apiKeys.openai) {
            providerFetchers.push({
                provider: 'openai',
                promise: fetchFromOpenAI(apiKeys.openai).catch((err) => {
                    console.warn('[ConfigService] OpenAI fetch failed:', err);
                    return [];
                }),
            });
        }
        if (apiKeys.google) {
            providerFetchers.push({
                provider: 'google',
                promise: fetchFromGoogle(apiKeys.google).catch((err) => {
                    console.warn('[ConfigService] Google fetch failed:', err);
                    return [];
                }),
            });
        }
        if (apiKeys.groq) {
            providerFetchers.push({
                provider: 'groq',
                promise: fetchFromGroq(apiKeys.groq).catch((err) => {
                    console.warn('[ConfigService] Groq fetch failed:', err);
                    return [];
                }),
            });
        }
        if (apiKeys.deepinfra) {
            providerFetchers.push({
                provider: 'deepinfra',
                promise: fetchFromDeepinfra(apiKeys.deepinfra).catch((err) => {
                    console.warn('[ConfigService] DeepInfra fetch failed:', err);
                    return [];
                }),
            });
        }
        // Wait for all fetches to complete
        const [metadataMap, llmPricesConfig, genaiPricingResult, ...providerResults] = await Promise.all([
            metadataMapPromise,
            llmPricesPromise,
            genaiPricingPromise,
            ...providerFetchers.map((f) => f.promise),
        ]);
        // Store pricing data
        this.llmPricesConfig = llmPricesConfig;
        this.genaiPricing = genaiPricingResult;
        // STEP 3: Merge - Enrich provider models with OpenRouter metadata
        const enrichedModels = [];
        for (let i = 0; i < providerFetchers.length; i++) {
            const { provider } = providerFetchers[i];
            const models = providerResults[i];
            for (const model of models) {
                const enriched = this.enrichModelWithMetadata(model, metadataMap);
                enrichedModels.push(enriched);
            }
            console.log(`[ConfigService] ${provider}: ${models.length} models loaded`);
        }
        // Store in cache
        this.modelCache = enrichedModels;
        // Also update the legacy config with enriched models for backwards compatibility
        await this.updateConfigWithEnrichedModels(enrichedModels);
        console.log(`[ConfigService] Rosetta Stone complete: ${enrichedModels.length} total models`);
        return enrichedModels;
    }
    /**
     * Enrich a model from provider API with metadata
     * - OpenRouter: display name, context window, max completion tokens, reasoning model
     * - llm-prices.com: pricing (primary source)
     * - groq.com/pricing: pricing fallback for Groq models (not in llm-prices.com)
     */
    enrichModelWithMetadata(model, metadataMap) {
        // normalizeModelId() strips date suffixes, so "gpt-5-nano-2025-08-07" -> "gpt-5-nano"
        const normalizedId = normalizeModelId(model.id);
        // Also try with dot-format version (claude-3-5 → claude-3.5) for OpenRouter lookup
        const normalizedIdWithDots = this.normalizePricingId(model.id);
        // Try multiple lookup strategies for metadata
        const metadata = metadataMap.get(normalizedId)
            || metadataMap.get(normalizedIdWithDots)
            || metadataMap.get(model.id.toLowerCase());
        // Get pricing from llm-prices.com (primary) or groq.com (fallback for Groq)
        const pricing = this.getPricingFromLLMPrices(model.id);
        if (metadata) {
            return {
                id: model.id, // Keep the REAL provider ID
                provider: model.provider,
                displayName: metadata.displayName || model.displayName,
                contextWindow: model.contextWindow ?? metadata.contextWindow,
                maxCompletionTokens: model.maxCompletionTokens ?? metadata.maxCompletionTokens,
                pricing, // From llm-prices.com only
                isReasoningModel: model.isReasoningModel ?? metadata.isReasoningModel,
            };
        }
        // No metadata found - use provider data only
        return {
            id: model.id,
            provider: model.provider,
            displayName: model.displayName,
            contextWindow: model.contextWindow,
            maxCompletionTokens: model.maxCompletionTokens,
            pricing, // From llm-prices.com only
            isReasoningModel: model.isReasoningModel,
        };
    }
    /**
     * Normalize model ID for pricing lookup
     * Converts provider API format to llm-prices.com format:
     * - `claude-3-5-haiku-20241022` → `claude-3.5-haiku`
     * - `claude-opus-4-5-20250514` → `claude-opus-4.5`
     * - `gpt-4o-2024-08-06` → `gpt-4o`
     */
    normalizePricingId(modelId) {
        // Step 1: Start with base normalization (removes provider prefix, date suffixes, lowercase)
        let normalized = normalizeModelId(modelId);
        // Step 2: Convert Anthropic version format: "3-5" → "3.5", "4-5" → "4.5"
        // This handles: claude-3-5-haiku → claude-3.5-haiku
        //               claude-opus-4-5 → claude-opus-4.5
        normalized = normalized.replace(/(\d)-(\d)/g, '$1.$2');
        return normalized;
    }
    /**
     * Get pricing for a model from pydantic/genai-prices
     * Falls back to llm-prices.com if genai-prices doesn't have the model
     */
    getPricingFromLLMPrices(modelId) {
        const normalizedId = this.normalizePricingId(modelId);
        const modelIdLower = modelId.toLowerCase();
        // Primary source: pydantic/genai-prices (CORS-friendly)
        if (this.genaiPricing) {
            // Try exact match
            if (this.genaiPricing.models[modelId]) {
                return this.genaiPricing.models[modelId];
            }
            // Try normalized ID
            if (this.genaiPricing.models[normalizedId]) {
                return this.genaiPricing.models[normalizedId];
            }
            // Try lowercase
            if (this.genaiPricing.models[modelIdLower]) {
                return this.genaiPricing.models[modelIdLower];
            }
            // Try fuzzy match
            for (const [id, pricing] of Object.entries(this.genaiPricing.models)) {
                const idLower = id.toLowerCase();
                if (idLower.includes(normalizedId) || normalizedId.includes(idLower)) {
                    return pricing;
                }
                if (idLower.includes(modelIdLower) || modelIdLower.includes(idLower)) {
                    return pricing;
                }
            }
            // Handle Claude word order: claude-haiku-4.5 → claude-4.5-haiku
            const claudeMatch = normalizedId.match(/^claude-(haiku|sonnet|opus)-(\d+\.?\d*)$/);
            if (claudeMatch) {
                const [, modelType, version] = claudeMatch;
                const altFormats = [
                    `claude-${version}-${modelType}`,
                    `claude${version}${modelType}`,
                ];
                for (const alt of altFormats) {
                    if (this.genaiPricing.models[alt]) {
                        return this.genaiPricing.models[alt];
                    }
                }
            }
        }
        // Secondary source: llm-prices.com
        if (this.llmPricesConfig) {
            if (this.llmPricesConfig.models[modelId]?.pricing) {
                return this.llmPricesConfig.models[modelId].pricing;
            }
            if (this.llmPricesConfig.models[normalizedId]?.pricing) {
                return this.llmPricesConfig.models[normalizedId].pricing;
            }
            if (this.llmPricesConfig.models[modelIdLower]?.pricing) {
                return this.llmPricesConfig.models[modelIdLower].pricing;
            }
        }
        return null;
    }
    /**
     * Update the legacy LLMProviderConfig with enriched models
     * This ensures backwards compatibility with existing code using getConfig()
     */
    async updateConfigWithEnrichedModels(enrichedModels) {
        const config = await this.getConfig();
        // Add/update models in config
        for (const model of enrichedModels) {
            config.models[model.id] = {
                id: model.id,
                provider: model.provider,
                displayName: model.displayName,
                contextWindow: model.contextWindow,
                maxCompletionTokens: model.maxCompletionTokens,
                pricing: model.pricing,
                capabilities: [],
                zeroDataRetention: model.provider === 'groq' || model.provider === 'deepinfra' ? true : null,
                isReasoningModel: model.isReasoningModel,
            };
        }
        // Re-cache the updated config
        await this.cacheConfig(config);
    }
    /**
     * Get cached models (synchronous)
     * Returns the models from the last refreshModels() call
     *
     * @returns Array of EnrichedModel (empty if refreshModels not called yet)
     */
    getModels() {
        return this.modelCache;
    }
    /**
     * Get pricing for a model (synchronous lookup from cache)
     * Use this for CostTracker integration
     *
     * @param modelId - The model ID to look up
     * @returns Pricing data or null if not found
     */
    getPricingSync(modelId) {
        // First check the enriched model cache (which gets pricing from llm-prices.com)
        const model = this.modelCache.find((m) => m.id === modelId);
        if (model?.pricing) {
            return model.pricing;
        }
        // Fall back to llm-prices.com lookup (ONLY source for pricing)
        return this.getPricingFromLLMPrices(modelId);
    }
}
/**
 * Singleton instance
 */
export const configService = new ConfigServiceImpl();
/**
 * Export class for testing
 */
export { ConfigServiceImpl };
